import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { X, AlertTriangle, Send, Loader2 } from 'lucide-react';

const REPORT_REASONS = [
  'incorrect_information',
  'offensive_content',
  'spam',
  'duplicate',
  'scam_fraud',
  'other',
];

const ReportModal = ({ isOpen, onClose, targetType, targetId, targetName }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setError('');
    if (!reason) { setError(t('reportReasonRequired')); return; }
    if (details.length < 10) { setError(t('reportDetailsMin')); return; }
    setSubmitting(true);
    try {
      await axiosInstance.post('/reports', { targetType, targetId, reason, details });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('already have an open report')) {
        setError(t('reportAlreadyExists'));
      } else {
        setError(typeof detail === 'string' ? detail : t('reportSubmitError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setDetails('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="report-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-bold text-gray-900" data-testid="report-modal-title">
                {t('reportTitle')}
              </h3>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="report-modal-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6" data-testid="report-success">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-gray-900 font-medium mb-1">{t('reportSubmitted')}</p>
              <p className="text-sm text-gray-500">{t('reportSubmittedDesc')}</p>
              <button onClick={handleClose} className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm" data-testid="report-done-btn">
                {t('done')}
              </button>
            </div>
          ) : (
            <>
              {targetName && (
                <p className="text-sm text-gray-500 mb-4">
                  {t('reportingItem', { name: targetName })}
                </p>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('reportReason')}</label>
                <div className="space-y-2" data-testid="report-reason-list">
                  {REPORT_REASONS.map(r => (
                    <label
                      key={r}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        reason === r ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`report-reason-${r}`}
                    >
                      <input
                        type="radio"
                        name="reportReason"
                        value={r}
                        checked={reason === r}
                        onChange={() => setReason(r)}
                        className="text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{t(`reportReason_${r}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reportDetails')}</label>
                <textarea
                  value={details}
                  onChange={e => setDetails(e.target.value)}
                  placeholder={t('reportDetailsPlaceholder')}
                  maxLength={1000}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                  data-testid="report-details-input"
                />
                <p className="text-xs text-gray-400 mt-1">{t('charsRemaining', { count: 1000 - details.length })}</p>
              </div>

              {error && <p className="text-sm text-red-600 mb-3" data-testid="report-error">{error}</p>}

              <div className="flex gap-3">
                <button onClick={handleClose} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm">
                  {t('cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  data-testid="report-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {submitting ? t('submitting') : t('submitReport')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
